// Borradores — everything unsent, across every conversation, newest first (ios/Sources/Views/
// DraftsSheet.swift, DraftsPresentation). A row opens its conversation; a THREAD draft says so and
// opens its thread, because landing in the conversation with the reply's text would be the same
// wrong-place failure the separate drafts exist to prevent. Deleting is the two-tap confirm.
import { useMemo } from 'react';
import { data } from '../../../bridge/client';
import { t } from '../../../i18n/i18n';
import { ConfirmButton, DestinationFrame, EmptyState } from '../../kit/Kit';
import { useLive, useMinuteClock } from '../../hooks';
import { useShell } from '../../shell/ShellContext';
import { buildDraftRows, type DraftListRow } from './destinationsModel';
import { PlaceLabel } from './PlaceLabel';
import { Icon } from '../../../design/icons';
import './destinations.css';

export function DraftsView() {
  const shell = useShell();
  const now = useMinuteClock();
  const { value: snapshot } = useLive(() => data.call('draftsSnapshot'), ['drafts', 'channels', 'users', 'channelParticipants'], []);
  const rows = useMemo(() => (snapshot ? buildDraftRows(snapshot, now) : null), [snapshot, now]);

  const open = (row: DraftListRow) => {
    if (row.rootId) shell.openThread(row.rootId);
    else shell.openChannel(row.channelId);
  };

  return (
    <DestinationFrame icon="pencilLine" title={t('drafts.title')}>
      {rows === null ? null : !rows.length ? (
        <EmptyState title={t('drafts.empty')} />
      ) : (
        <div className="dest-list" role="list">
          {rows.map((row) => (
            <div
              key={row.id}
              role="listitem"
              tabIndex={0}
              className="dest-row draft-row"
              onClick={() => open(row)}
              onKeyDown={(e) => (e.key === 'Enter' && e.target === e.currentTarget ? open(row) : undefined)}
            >
              <span className="draft-glyph" aria-hidden="true">
                <Icon name={row.isThreadReply ? 'threads' : 'pencilLine'} size={16} />
              </span>
              <div className="dest-row-main">
                <div className="dest-row-head">
                  <PlaceLabel name={row.title} type={row.placeType} />
                  <span className="dest-time">{row.timeLabel}</span>
                </div>
                <div className="dest-preview">{row.preview}</div>
              </div>
              <div className="dest-row-trailing" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <ConfirmButton icon="trash" label={t('common.delete')} confirmLabel={t('channel.messageDiscardConfirm')} onConfirm={() => void data.call('deleteDraft', row.channelId, row.rootId)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </DestinationFrame>
  );
}
